import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type MetricsCardProps = {
  title: string;
  value: string | number;
  description?: string;
  data?: Array<{ [key: string]: any }>;
  dataKey: string;
  chartColor?: string;
  percentageChange?: number;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
};

export function MetricsCard({
  title,
  value,
  description,
  data,
  dataKey,
  chartColor = "var(--nutrition-green-500)",
  percentageChange,
  icon,
  className,
  onClick,
}: MetricsCardProps) {
  return (
    <Card
      className={cn(className, onClick && 'cursor-pointer hover:shadow-lg transition-shadow')}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? title : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon && <div>{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-2xl font-bold">{value}</div>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            {percentageChange !== undefined && (
              <div className={cn(
                "text-xs font-medium",
                percentageChange >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {percentageChange >= 0 ? "+" : ""}{percentageChange}% from last month
              </div>
            )}
          </div>
        </div>

        {data && (
          <div className="h-[60px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <XAxis
                  dataKey="month"
                  fontSize={10}
                  stroke="#888888"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-md border bg-white p-2 shadow-md">
                          <div className="text-sm font-bold">{payload[0].payload.month}</div>
                          <div className="text-xs">
                            {title}: {payload[0].value}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey={dataKey}
                  fill={chartColor}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
